import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Flex, Text, Button, Badge, Spinner, Center, HStack, VStack, Select, Input,
  IconButton, Tooltip, SimpleGrid, Divider, Table, Thead, Tbody, Tr, Th, Td,
  Tabs, TabList, TabPanels, Tab, TabPanel, Checkbox, InputGroup, InputLeftElement,
  Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton,
  Alert, AlertIcon, Progress, useToast,
} from "@chakra-ui/react";
import {
  RefreshCw, Search, ArrowUp, ArrowDown, TrendingUp, TrendingDown, ExternalLink,
  ListFilter, Info,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getDivergenciasPrecoOpcoes, getDivergenciasPrecoFornecedores,
  getDivergenciasPrecoListas, getDivergenciasPrecoItens,
} from "../services/api";
import { formatBRL, formatDate } from "../utils/formatters";

const ACCENT = "#F68633";
const VERDE = "#01695E";

const PERIODOS = [
  { label: "30 dias", dias: 30 },
  { label: "60 dias", dias: 60 },
  { label: "90 dias", dias: 90 },
  { label: "6 meses", dias: 180 },
  { label: "Tudo", dias: null },
];

const isoDiasAtras = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
};

const pct = (v) => `${v > 0 ? "+" : ""}${Number(v || 0).toFixed(2)}%`;

// Fornecedor cobrando acima do custo cadastrado é o caso que dói: a peça entra
// mais cara do que a precificação assume. Abaixo é sobra, mas ainda é preço
// errado no cadastro — por isso os dois aparecem, com pesos visuais diferentes.
const corDesvio = (v) => {
  const a = Math.abs(Number(v) || 0);
  if (a < 1) return "gray";
  if (Number(v) < 0) return "blue";
  if (a < 5) return "yellow";
  if (a < 15) return "orange";
  return "red";
};

const corImpacto = (v) => (Number(v) > 0 ? "red" : Number(v) < 0 ? "blue" : "gray");

function Kpi({ label, valor, ajuda, cor = VERDE }) {
  return (
    <Box bg="white" borderRadius="lg" boxShadow="sm" p={4} borderTop="3px solid" borderTopColor={cor}>
      <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" letterSpacing="wide">
        {label}
      </Text>
      <Text fontSize="2xl" fontWeight="bold" color={cor} lineHeight="1.2" mt={1}>
        {valor}
      </Text>
      {ajuda && <Text fontSize="xs" color="gray.500" mt={1}>{ajuda}</Text>}
    </Box>
  );
}

// Cabeçalho de coluna que ordena ao clicar. As colunas de desvio/impacto o
// backend ordena por módulo — "quem erra mais", para cima ou para baixo.
function ThOrd({ campo, ordem, setOrdem, children, isNumeric, ajuda }) {
  const ativo = ordem.by === campo;
  const conteudo = (
    <HStack spacing={1} justify={isNumeric ? "flex-end" : "flex-start"}>
      <Text fontSize="xs">{children}</Text>
      {ativo && (ordem.dir === "desc" ? <ArrowDown size={12} /> : <ArrowUp size={12} />)}
    </HStack>
  );
  return (
    <Th
      isNumeric={isNumeric}
      cursor="pointer"
      userSelect="none"
      color={ativo ? ACCENT : "gray.600"}
      onClick={() => setOrdem({ by: campo, dir: ativo && ordem.dir === "desc" ? "asc" : "desc" })}
      whiteSpace="nowrap"
    >
      {ajuda ? <Tooltip label={ajuda} placement="top" hasArrow>{conteudo}</Tooltip> : conteudo}
    </Th>
  );
}

export default function DivergenciasPreco() {
  const toast = useToast();
  const navigate = useNavigate();

  const [opcoes, setOpcoes] = useState({ fornecedores: [], periodo: null });
  const [filtros, setFiltros] = useState({
    dias: 90,
    data_inicio: isoDiasAtras(90),
    data_fim: "",
    fornecedor_id: "",
    tolerancia_pct: 0,
    incluir_alternativas: false,
    busca: "",
  });

  const [aba, setAba] = useState(0);
  const [dadosForn, setDadosForn] = useState(null);
  const [dadosListas, setDadosListas] = useState(null);
  const [carregando, setCarregando] = useState(true);

  const [ordemForn, setOrdemForn] = useState({ by: "impacto_total", dir: "desc" });
  const [ordemLista, setOrdemLista] = useState({ by: "impacto_total", dir: "desc" });

  // Drill-down
  const [drill, setDrill] = useState(null); // { tipo: 'fornecedor'|'lista', id, titulo }
  const [drillDados, setDrillDados] = useState(null);
  const [drillCarregando, setDrillCarregando] = useState(false);
  const [drillOrdem, setDrillOrdem] = useState({ by: "impacto", dir: "desc" });
  const [drillPagina, setDrillPagina] = useState(1);
  const [drillApenasDiv, setDrillApenasDiv] = useState(true);
  const [drillDirecao, setDrillDirecao] = useState("");
  const [drillBusca, setDrillBusca] = useState("");

  const params = useMemo(() => {
    const p = {};
    if (filtros.data_inicio) p.data_inicio = filtros.data_inicio;
    if (filtros.data_fim) p.data_fim = filtros.data_fim;
    if (filtros.fornecedor_id) p.fornecedor_id = filtros.fornecedor_id;
    if (Number(filtros.tolerancia_pct) > 0) p.tolerancia_pct = filtros.tolerancia_pct;
    if (filtros.incluir_alternativas) p.incluir_alternativas = 1;
    if (filtros.busca) p.busca = filtros.busca;
    return p;
  }, [filtros]);

  useEffect(() => {
    getDivergenciasPrecoOpcoes().then((d) => d && setOpcoes(d));
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const [f, l] = await Promise.all([
      getDivergenciasPrecoFornecedores({ ...params, order_by: ordemForn.by, order_dir: ordemForn.dir }),
      getDivergenciasPrecoListas({ ...params, order_by: ordemLista.by, order_dir: ordemLista.dir }),
    ]);
    if (f) setDadosForn(f); else toast({ title: "Erro ao carregar a análise", status: "error", duration: 4000 });
    if (l) setDadosListas(l);
    setCarregando(false);
  }, [params, ordemForn, ordemLista, toast]);

  useEffect(() => { carregar(); }, [carregar]);

  const carregarDrill = useCallback(async () => {
    if (!drill) return;
    setDrillCarregando(true);
    const extra = drill.tipo === "fornecedor" ? { fornecedor_id: drill.id } : { lista_id: drill.id };
    const d = await getDivergenciasPrecoItens({
      ...params, ...extra,
      busca: drillBusca || params.busca,
      apenas_divergentes: drillApenasDiv ? 1 : 0,
      direcao_desvio: drillDirecao || undefined,
      order_by: drillOrdem.by,
      order_dir: drillOrdem.dir,
      page: drillPagina,
      per_page: 50,
    });
    setDrillDados(d || null);
    setDrillCarregando(false);
  }, [drill, params, drillOrdem, drillPagina, drillApenasDiv, drillDirecao, drillBusca]);

  useEffect(() => { carregarDrill(); }, [carregarDrill]);

  const abrirDrill = (tipo, id, titulo) => {
    setDrillPagina(1);
    setDrillBusca("");
    setDrill({ tipo, id, titulo });
  };

  const aplicarPeriodo = (dias) => {
    setFiltros((f) => ({
      ...f,
      dias,
      data_inicio: dias ? isoDiasAtras(dias) : "",
      data_fim: "",
    }));
  };

  const resumo = (aba === 0 ? dadosForn : dadosListas)?.resumo;

  return (
    <Box ml={{ base: 0, md: "64px" }} pt="70px" px={{ base: 3, md: 6 }} pb={10} minH="100vh" bg="gray.50">
      <Flex justify="space-between" align="center" mb={3} gap={3} flexWrap="wrap">
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color={VERDE}>Divergências de Preço</Text>
          <Text fontSize="sm" color="gray.500">
            O que o fornecedor cobrou nas listas de compras contra o preço de custo que temos cadastrado.
          </Text>
        </Box>
        <HStack flexWrap="wrap">
          <Button size="sm" variant="outline" onClick={() => navigate("/listas-compras/dashboard")}>
            Listas de Compras
          </Button>
          <Button size="sm" leftIcon={<RefreshCw size={14} />} onClick={carregar} isLoading={carregando}
            bg={ACCENT} color="white" _hover={{ bg: "#e07728" }}>
            Atualizar
          </Button>
        </HStack>
      </Flex>

      {/* Filtros */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" p={4} mb={4}>
        <Flex gap={3} flexWrap="wrap" align="flex-end">
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>Período</Text>
            <HStack spacing={1}>
              {PERIODOS.map((p) => (
                <Button key={p.label} size="xs" variant={filtros.dias === p.dias ? "solid" : "outline"}
                  colorScheme={filtros.dias === p.dias ? "orange" : "gray"}
                  onClick={() => aplicarPeriodo(p.dias)}>
                  {p.label}
                </Button>
              ))}
            </HStack>
          </Box>

          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>De</Text>
            <Input size="sm" type="date" w="150px" value={filtros.data_inicio}
              onChange={(e) => setFiltros((f) => ({ ...f, data_inicio: e.target.value, dias: -1 }))} />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>Até</Text>
            <Input size="sm" type="date" w="150px" value={filtros.data_fim}
              onChange={(e) => setFiltros((f) => ({ ...f, data_fim: e.target.value, dias: -1 }))} />
          </Box>

          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>Fornecedor</Text>
            <Select size="sm" w="190px" value={filtros.fornecedor_id}
              onChange={(e) => setFiltros((f) => ({ ...f, fornecedor_id: e.target.value }))}>
              <option value="">Todos</option>
              {opcoes.fornecedores?.map((f) => (
                <option key={f.fornecedor_id} value={f.fornecedor_id}>{f.nome}</option>
              ))}
            </Select>
          </Box>

          <Box>
            <Tooltip label="Diferença menor que isso conta como preço certo. Serve para ignorar centavo de arredondamento." hasArrow>
              <HStack spacing={1} mb={1} color="gray.500">
                <Text fontSize="xs">Tolerância</Text>
                <Info size={11} />
              </HStack>
            </Tooltip>
            <Select size="sm" w="120px" value={filtros.tolerancia_pct}
              onChange={(e) => setFiltros((f) => ({ ...f, tolerancia_pct: e.target.value }))}>
              <option value={0}>Qualquer</option>
              <option value={1}>± 1%</option>
              <option value={3}>± 3%</option>
              <option value={5}>± 5%</option>
              <option value={10}>± 10%</option>
            </Select>
          </Box>

          <Box flex="1" minW="200px">
            <Text fontSize="xs" color="gray.500" mb={1}>Buscar peça</Text>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none"><Search size={14} color="#A0AEC0" /></InputLeftElement>
              <Input placeholder="código ou nome" value={filtros.busca}
                onChange={(e) => setFiltros((f) => ({ ...f, busca: e.target.value }))} />
            </InputGroup>
          </Box>

          <Tooltip label="Inclui as peças que o fornecedor trocou por outra (código ou marca diferente). Servem para ver o comportamento dele, mas não para corrigir o custo da peça pedida." hasArrow>
            <Box>
              <Checkbox size="sm" isChecked={filtros.incluir_alternativas}
                onChange={(e) => setFiltros((f) => ({ ...f, incluir_alternativas: e.target.checked }))}>
                <Text fontSize="sm">Incluir alternativas</Text>
              </Checkbox>
            </Box>
          </Tooltip>
        </Flex>
      </Box>

      {/* KPIs */}
      {resumo && (
        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3} mb={4}>
          <Kpi label="Itens analisados" valor={resumo.itens.toLocaleString("pt-BR")}
            ajuda={`${resumo.listas?.toLocaleString("pt-BR") || 0} listas`} />
          <Kpi label="Com divergência" valor={`${resumo.divergentes.toLocaleString("pt-BR")}`}
            ajuda={`${resumo.pct_divergentes}% dos itens`} cor={resumo.pct_divergentes > 20 ? "#C53030" : ACCENT} />
          <Kpi label="Desvio ponderado" valor={pct(resumo.desvio_ponderado_pct)}
            ajuda="quanto o custo real ficou acima do cadastrado"
            cor={resumo.desvio_ponderado_pct > 0 ? "#C53030" : "#2B6CB0"} />
          <Kpi label="Impacto no período" valor={formatBRL(resumo.impacto_total)}
            ajuda="soma do que veio a mais (ou a menos)"
            cor={resumo.impacto_total > 0 ? "#C53030" : "#2B6CB0"} />
          <Kpi label="Cobraram a mais" valor={resumo.acima.toLocaleString("pt-BR")}
            ajuda={formatBRL(resumo.impacto_acima)} cor="#C53030" />
          <Kpi label="Cobraram a menos" valor={resumo.abaixo.toLocaleString("pt-BR")}
            ajuda={formatBRL(resumo.impacto_abaixo)} cor="#2B6CB0" />
        </SimpleGrid>
      )}

      <Box bg="white" borderRadius="lg" boxShadow="sm">
        <Tabs colorScheme="orange" index={aba} onChange={setAba}>
          <TabList px={4}>
            <Tab fontWeight="bold" fontSize="sm">Por fornecedor</Tab>
            <Tab fontWeight="bold" fontSize="sm">Por lista de compras</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0} py={0}>
              <TabelaFornecedores
                dados={dadosForn} carregando={carregando} ordem={ordemForn} setOrdem={setOrdemForn}
                onAbrir={(f) => abrirDrill("fornecedor", f.fornecedor_id, f.fornecedor_nome)}
              />
            </TabPanel>
            <TabPanel px={0} py={0}>
              <TabelaListas
                dados={dadosListas} carregando={carregando} ordem={ordemLista} setOrdem={setOrdemLista}
                onAbrir={(l) => abrirDrill("lista", l.lista_id, `Lista #${l.lista_id} · ${l.fornecedor_nome}`)}
                navigate={navigate}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      <Drawer isOpen={!!drill} placement="right" size="xl" onClose={() => { setDrill(null); setDrillDados(null); }}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" pb={3}>
            <Text fontSize="md" color={VERDE}>{drill?.titulo}</Text>
            <Text fontSize="xs" color="gray.500" fontWeight="normal">
              Item a item, do que mais pesou para o que menos pesou.
            </Text>
          </DrawerHeader>
          <DrawerBody px={0} py={0}>
            <DetalheItens
              dados={drillDados} carregando={drillCarregando}
              ordem={drillOrdem} setOrdem={(o) => { setDrillOrdem(o); setDrillPagina(1); }}
              pagina={drillPagina} setPagina={setDrillPagina}
              apenasDiv={drillApenasDiv} setApenasDiv={(v) => { setDrillApenasDiv(v); setDrillPagina(1); }}
              direcao={drillDirecao} setDirecao={(v) => { setDrillDirecao(v); setDrillPagina(1); }}
              busca={drillBusca} setBusca={(v) => { setDrillBusca(v); setDrillPagina(1); }}
              navigate={navigate}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}

function TabelaFornecedores({ dados, carregando, ordem, setOrdem, onAbrir }) {
  if (carregando && !dados) return <Center py={16}><Spinner color={ACCENT} thickness="3px" /></Center>;
  const linhas = dados?.fornecedores || [];
  if (!linhas.length) {
    return (
      <Box p={6}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          Nenhuma resposta de fornecedor com preço de custo cadastrado no período escolhido.
        </Alert>
      </Box>
    );
  }

  return (
    <Box overflowX="auto">
      <Table size="sm" variant="simple">
        <Thead bg="gray.50">
          <Tr>
            <ThOrd campo="fornecedor_nome" ordem={ordem} setOrdem={setOrdem}>Fornecedor</ThOrd>
            <ThOrd campo="itens" ordem={ordem} setOrdem={setOrdem} isNumeric>Itens</ThOrd>
            <ThOrd campo="pct_divergentes" ordem={ordem} setOrdem={setOrdem} isNumeric
              ajuda="Quantos itens vieram com preço diferente do cadastrado.">% divergentes</ThOrd>
            <ThOrd campo="desvio_ponderado_pct" ordem={ordem} setOrdem={setOrdem} isNumeric
              ajuda="Diferença total em reais dividida pelo custo total. É o número que diz quanto o fornecedor cobra acima do cadastro.">Desvio ponderado</ThOrd>
            <ThOrd campo="desvio_mediano_pct" ordem={ordem} setOrdem={setOrdem} isNumeric
              ajuda="Mediana do desvio dos itens que divergiram — o desvio típico, sem o peso dos extremos.">Desvio típico</ThOrd>
            <ThOrd campo="impacto_total" ordem={ordem} setOrdem={setOrdem} isNumeric
              ajuda="Quanto a mais (ou a menos) foi pago no período por causa da diferença.">Impacto</ThOrd>
            <ThOrd campo="valor_referencia_total" ordem={ordem} setOrdem={setOrdem} isNumeric>Custo previsto</ThOrd>
            <ThOrd campo="listas" ordem={ordem} setOrdem={setOrdem} isNumeric>Listas</ThOrd>
            <Th></Th>
          </Tr>
        </Thead>
        <Tbody>
          {linhas.map((f) => (
            <Tr key={f.fornecedor_id} _hover={{ bg: "orange.50" }} cursor="pointer" onClick={() => onAbrir(f)}>
              <Td>
                <Text fontWeight="bold" fontSize="sm">{f.fornecedor_nome}</Text>
                {f.pior_item && (
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    pior: {f.pior_item.nome_produto} ({pct(f.pior_item.desvio_pct)})
                  </Text>
                )}
              </Td>
              <Td isNumeric fontSize="sm">{f.itens.toLocaleString("pt-BR")}</Td>
              <Td isNumeric>
                <VStack spacing={0} align="flex-end">
                  <Text fontSize="sm" fontWeight="bold">{f.pct_divergentes}%</Text>
                  <Progress value={f.pct_divergentes} size="xs" w="52px" borderRadius="full"
                    colorScheme={f.pct_divergentes > 25 ? "red" : f.pct_divergentes > 10 ? "orange" : "green"} />
                </VStack>
              </Td>
              <Td isNumeric>
                <Badge colorScheme={corDesvio(f.desvio_ponderado_pct)} fontSize="0.75rem" px={2}>
                  {pct(f.desvio_ponderado_pct)}
                </Badge>
              </Td>
              <Td isNumeric fontSize="sm" color="gray.600">{f.divergentes ? pct(f.desvio_mediano_pct) : "—"}</Td>
              <Td isNumeric>
                <HStack spacing={1} justify="flex-end">
                  {f.impacto_total > 0 ? <TrendingUp size={13} color="#C53030" /> : f.impacto_total < 0 ? <TrendingDown size={13} color="#2B6CB0" /> : null}
                  <Text fontSize="sm" fontWeight="bold" color={`${corImpacto(f.impacto_total)}.600`}>
                    {formatBRL(f.impacto_total)}
                  </Text>
                </HStack>
              </Td>
              <Td isNumeric fontSize="sm" color="gray.600">{formatBRL(f.valor_referencia_total)}</Td>
              <Td isNumeric fontSize="sm" color="gray.600">{f.listas}</Td>
              <Td>
                <Tooltip label="Ver item a item" hasArrow>
                  <IconButton size="xs" variant="ghost" aria-label="Detalhar" icon={<ListFilter size={14} />} />
                </Tooltip>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      <Box px={4} py={3}>
        <Text fontSize="xs" color="gray.500">
          Clique em uma linha para ver quais peças puxaram a divergência daquele fornecedor.
        </Text>
      </Box>
    </Box>
  );
}

function TabelaListas({ dados, carregando, ordem, setOrdem, onAbrir, navigate }) {
  if (carregando && !dados) return <Center py={16}><Spinner color={ACCENT} thickness="3px" /></Center>;
  const linhas = dados?.listas || [];
  if (!linhas.length) {
    return (
      <Box p={6}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          Nenhuma lista de compras respondida no período escolhido.
        </Alert>
      </Box>
    );
  }

  return (
    <Box overflowX="auto">
      <Table size="sm" variant="simple">
        <Thead bg="gray.50">
          <Tr>
            <ThOrd campo="lista_id" ordem={ordem} setOrdem={setOrdem}>Lista</ThOrd>
            <ThOrd campo="data_lista" ordem={ordem} setOrdem={setOrdem}>Data</ThOrd>
            <ThOrd campo="fornecedor_nome" ordem={ordem} setOrdem={setOrdem}>Fornecedor</ThOrd>
            <ThOrd campo="itens" ordem={ordem} setOrdem={setOrdem} isNumeric>Itens</ThOrd>
            <ThOrd campo="divergentes" ordem={ordem} setOrdem={setOrdem} isNumeric>Divergentes</ThOrd>
            <ThOrd campo="desvio_ponderado_pct" ordem={ordem} setOrdem={setOrdem} isNumeric>Desvio ponderado</ThOrd>
            <ThOrd campo="impacto_total" ordem={ordem} setOrdem={setOrdem} isNumeric>Impacto</ThOrd>
            <ThOrd campo="valor_referencia_total" ordem={ordem} setOrdem={setOrdem} isNumeric>Custo previsto</ThOrd>
            <Th></Th>
          </Tr>
        </Thead>
        <Tbody>
          {linhas.map((l) => (
            <Tr key={l.lista_id} _hover={{ bg: "orange.50" }} cursor="pointer" onClick={() => onAbrir(l)}>
              <Td fontSize="sm" fontWeight="bold">#{l.lista_id}</Td>
              <Td fontSize="sm">{formatDate(l.data_lista)}</Td>
              <Td fontSize="sm">{l.fornecedor_nome}</Td>
              <Td isNumeric fontSize="sm">{l.itens}</Td>
              <Td isNumeric>
                <Text fontSize="sm" fontWeight={l.divergentes ? "bold" : "normal"}
                  color={l.divergentes ? "orange.600" : "gray.500"}>
                  {l.divergentes} <Text as="span" color="gray.400" fontWeight="normal">({l.pct_divergentes}%)</Text>
                </Text>
              </Td>
              <Td isNumeric>
                <Badge colorScheme={corDesvio(l.desvio_ponderado_pct)} fontSize="0.75rem" px={2}>
                  {pct(l.desvio_ponderado_pct)}
                </Badge>
              </Td>
              <Td isNumeric fontSize="sm" fontWeight="bold" color={`${corImpacto(l.impacto_total)}.600`}>
                {formatBRL(l.impacto_total)}
              </Td>
              <Td isNumeric fontSize="sm" color="gray.600">{formatBRL(l.valor_referencia_total)}</Td>
              <Td>
                <Tooltip label="Abrir a lista de compras" hasArrow>
                  <IconButton size="xs" variant="ghost" aria-label="Abrir lista" icon={<ExternalLink size={14} />}
                    onClick={(e) => { e.stopPropagation(); navigate(`/listas-compras/${l.lista_id}`); }} />
                </Tooltip>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

function DetalheItens({ dados, carregando, ordem, setOrdem, pagina, setPagina, apenasDiv, setApenasDiv, direcao, setDirecao, busca, setBusca, navigate }) {
  const itens = dados?.itens || [];
  const pag = dados?.paginacao;
  const resumo = dados?.resumo;

  return (
    <Box>
      <Box px={4} py={3} bg="gray.50" borderBottomWidth="1px">
        <Flex gap={3} flexWrap="wrap" align="center">
          <Checkbox size="sm" isChecked={apenasDiv} onChange={(e) => setApenasDiv(e.target.checked)}>
            <Text fontSize="sm">Só os divergentes</Text>
          </Checkbox>
          <Select size="xs" w="150px" value={direcao} onChange={(e) => setDirecao(e.target.value)}>
            <option value="">Para cima e para baixo</option>
            <option value="acima">Cobraram a mais</option>
            <option value="abaixo">Cobraram a menos</option>
          </Select>
          <InputGroup size="xs" w="200px">
            <InputLeftElement pointerEvents="none"><Search size={12} color="#A0AEC0" /></InputLeftElement>
            <Input placeholder="filtrar peça" value={busca} onChange={(e) => setBusca(e.target.value)} />
          </InputGroup>
          {resumo && (
            <HStack spacing={4} ml="auto">
              <Text fontSize="xs" color="gray.600">
                <b>{resumo.itens}</b> itens
              </Text>
              <Text fontSize="xs" color={resumo.impacto_total > 0 ? "red.600" : "blue.600"}>
                impacto <b>{formatBRL(resumo.impacto_total)}</b>
              </Text>
              <Text fontSize="xs" color="gray.600">
                desvio ponderado <b>{pct(resumo.desvio_ponderado_pct)}</b>
              </Text>
            </HStack>
          )}
        </Flex>
      </Box>

      {carregando ? (
        <Center py={16}><Spinner color={ACCENT} thickness="3px" /></Center>
      ) : !itens.length ? (
        <Box p={6}>
          <Alert status="success" borderRadius="md">
            <AlertIcon />
            Nenhum item divergente com esses filtros.
          </Alert>
        </Box>
      ) : (
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead bg="white" position="sticky" top={0} zIndex={1}>
              <Tr>
                <ThOrd campo="nome_produto" ordem={ordem} setOrdem={setOrdem}>Peça</ThOrd>
                <ThOrd campo="data_lista" ordem={ordem} setOrdem={setOrdem}>Lista</ThOrd>
                <ThOrd campo="quantidade" ordem={ordem} setOrdem={setOrdem} isNumeric>Qtd</ThOrd>
                <ThOrd campo="preco_referencia" ordem={ordem} setOrdem={setOrdem} isNumeric>Custo cadastrado</ThOrd>
                <ThOrd campo="preco_informado" ordem={ordem} setOrdem={setOrdem} isNumeric>Cobrado</ThOrd>
                <ThOrd campo="desvio_pct" ordem={ordem} setOrdem={setOrdem} isNumeric>Desvio</ThOrd>
                <ThOrd campo="impacto" ordem={ordem} setOrdem={setOrdem} isNumeric>Impacto</ThOrd>
              </Tr>
            </Thead>
            <Tbody>
              {itens.map((i) => (
                <Tr key={`${i.resposta_id}`} _hover={{ bg: "gray.50" }}>
                  <Td maxW="280px">
                    <Text fontSize="sm" fontWeight="500" noOfLines={1}>{i.nome_produto}</Text>
                    <HStack spacing={2}>
                      <Text fontSize="xs" color="gray.500">{i.codigo_global}</Text>
                      {i.marca_solicitada && <Text fontSize="xs" color="gray.400">{i.marca_solicitada}</Text>}
                      {!i.e_confirmacao && <Badge fontSize="0.6rem" colorScheme="purple">alternativa</Badge>}
                    </HStack>
                  </Td>
                  <Td>
                    <Button size="xs" variant="link" colorScheme="orange"
                      onClick={() => navigate(`/listas-compras/${i.lista_id}`)}>
                      #{i.lista_id}
                    </Button>
                    <Text fontSize="xs" color="gray.500">{formatDate(i.data_lista)}</Text>
                  </Td>
                  <Td isNumeric fontSize="sm">{i.quantidade}</Td>
                  <Td isNumeric fontSize="sm" color="gray.600">{formatBRL(i.preco_referencia)}</Td>
                  <Td isNumeric fontSize="sm" fontWeight="bold">{formatBRL(i.preco_informado)}</Td>
                  <Td isNumeric>
                    <Badge colorScheme={corDesvio(i.desvio_pct)} fontSize="0.72rem">{pct(i.desvio_pct)}</Badge>
                  </Td>
                  <Td isNumeric fontSize="sm" fontWeight="bold" color={`${corImpacto(i.impacto)}.600`}>
                    {formatBRL(i.impacto)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {pag && pag.total_pages > 1 && (
            <>
              <Divider />
              <Flex px={4} py={3} justify="space-between" align="center">
                <Text fontSize="xs" color="gray.500">
                  {pag.total.toLocaleString("pt-BR")} itens · página {pag.page} de {pag.total_pages}
                </Text>
                <HStack>
                  <Button size="xs" isDisabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>Anterior</Button>
                  <Button size="xs" isDisabled={pagina >= pag.total_pages} onClick={() => setPagina(pagina + 1)}>Próxima</Button>
                </HStack>
              </Flex>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
